import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";

// fields: array of { name, label, type: text|number|textarea|select, options?, placeholder?, full? }
export function LeadForm({ type, fields = [], submitLabel = "Envoyer ma demande", vehicleId, compact, onSuccess, successExtra }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const baseFields = [
    { name: "name", label: "Nom complet", type: "text", required: true },
    { name: "phone", label: "Téléphone", type: "text", required: true },
    { name: "email", label: "E-mail", type: "text", required: true, full: true },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Merci de renseigner votre nom et votre e-mail.");
      return;
    }
    const details = {};
    fields.forEach((f) => {
      if (form[f.name] !== undefined && form[f.name] !== "") details[f.label] = form[f.name];
    });
    setLoading(true);
    try {
      const res = await api.post("/requests", {
        type,
        name: form.name,
        email: form.email,
        phone: form.phone || "",
        message: form.message || "",
        details,
        vehicle_id: vehicleId || null,
      });
      setDone(true);
      onSuccess?.(form);
      toast.success("Demande envoyée !", {
        description: res.data.email_sent
          ? "Nous vous recontacterons rapidement."
          : "Votre demande a bien été enregistrée.",
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-form-success" className="text-center py-12 px-6">
        <CheckCircle2 className="h-14 w-14 text-[#C5A059] mx-auto mb-4" />
        <h3 className="font-display text-2xl font-bold text-white">Merci !</h3>
        <p className="text-neutral-400 mt-2 max-w-sm mx-auto">
          Votre demande a bien été transmise à l'équipe AS AUTO. Nous vous recontacterons dans les meilleurs délais.
        </p>
        {successExtra}
      </div>
    );
  }

  const renderField = (f) => {
    const common = {
      "data-testid": `lead-field-${f.name}`,
      value: form[f.name] || "",
      onChange: (e) => setField(f.name, e.target.value),
      placeholder: f.placeholder,
      className: "bg-[#0a0a0a] border-white/15 focus-visible:ring-[#C5A059] text-white",
    };
    return (
      <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
        <Label className="text-neutral-300 mb-1.5 block text-sm">{f.label}{f.required && " *"}</Label>
        {f.type === "textarea" ? (
          <Textarea {...common} rows={4} />
        ) : f.type === "select" ? (
          <Select value={form[f.name] || ""} onValueChange={(v) => setField(f.name, v)}>
            <SelectTrigger data-testid={`lead-field-${f.name}`} className="bg-[#0a0a0a] border-white/15 text-white focus:ring-[#C5A059]">
              <SelectValue placeholder={f.placeholder || "Choisir"} />
            </SelectTrigger>
            <SelectContent>
              {f.options.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input type={f.type === "number" ? "number" : "text"} {...common} />
        )}
      </div>
    );
  };

  return (
    <form data-testid={`lead-form-${type}`} onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
      {baseFields.map(renderField)}
      {fields.map(renderField)}
      {!fields.some((f) => f.name === "message") && (
        <div className="sm:col-span-2">
          <Label className="text-neutral-300 mb-1.5 block text-sm">Message (optionnel)</Label>
          <Textarea
            data-testid="lead-field-message"
            value={form.message || ""}
            onChange={(e) => setField("message", e.target.value)}
            rows={4}
            className="bg-[#0a0a0a] border-white/15 focus-visible:ring-[#C5A059] text-white"
          />
        </div>
      )}
      <div className="sm:col-span-2">
        <Button
          type="submit"
          data-testid="lead-form-submit"
          disabled={loading}
          className="w-full sm:w-auto bg-[#C5A059] text-black hover:bg-[#E6C678] rounded-sm font-semibold px-8 h-12 active:scale-95 transition-transform"
        >
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
